import { yupResolver } from '@hookform/resolvers/yup';
import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import type { TFunction } from 'i18next';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { array, number, object, string } from 'yup';
import type { CreateLogRequestModalProps } from '../CreateLogRequestModal.model';

enum CollectFromOptions {
  Application = 'application',
  System = 'system',
}

export const collectFromOptionsArray = Object.values(CollectFromOptions) as CollectFromOptions[];
export interface CreateLogRequestFormularySchema {
  readonly title: string;
  readonly description: string;
  readonly collectFrom: CollectFromOptions[];
  readonly durationHour: number;
}

const defaultValuesFormulary: CreateLogRequestFormularySchema = {
  title: '',
  description: '',
  collectFrom: [],
  durationHour: 0,
};

function createSchema(t: TFunction) {
  return object().shape({
    title: string()
      .default('')
      .required(t('common:error.required'))
      .max(50, t('common:error.maxLength', { maxLength: 50 })),
    description: string()
      .optional()
      .transform((value: unknown) => {
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          return trimmedValue === '' ? undefined : trimmedValue;
        }
        return value;
      })
      .max(200, t('common:error.maxLength', { maxLength: 200 })),
    collectFrom: array()
      .required(t('common:error.required'))
      .default([])
      .of(string().oneOf(collectFromOptionsArray))
      .min(1),
    durationHour: number()
      .default(0)
      .required(t('common:error.required'))
      .positive(
        t('common:error.match', {
          regex: '[1-9][0-9]*',
          fieldName: t('logcat.creation.formularyFields.durationValidationField'),
        }),
      )
      .integer(
        t('common:error.match', {
          regex: '[1-9][0-9]*',
          fieldName: t('logcat.creation.formularyFields.durationValidationField'),
        }),
      )
      .typeError(
        t('common:error.match', {
          regex: '[1-9][0-9]*',
          fieldName: t('logcat.creation.formularyFields.durationValidationField'),
        }),
      )
      .min(1, t('common:error.min', { min: 1 }))
      .max(48, t('common:error.max', { max: 48 })),
  });
}

type UseFormularyOfCreateLogRequests = Pick<CreateLogRequestModalProps, 'isOpen'>;

export function useFormularyOfCreateLogRequests({
  isOpen,
}: UseFormularyOfCreateLogRequests): UseFormReturn<CreateLogRequestFormularySchema> {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));
  const form = useForm<CreateLogRequestFormularySchema>({
    mode: 'onChange',
    defaultValues: defaultValuesFormulary,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(createSchema(t)) as any,
  });

  useEffect(() => {
    //Reset if modal is closed
    if (!isOpen) {
      form.reset(defaultValuesFormulary);
    }
  }, [form, isOpen]);

  return form;
}
